var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Input, Flex, Heading, Spinner, FormControl, FormLabel, } from '@chakra-ui/react';
import { useState } from 'react';
const PLATE_FORMATS = [
    {
        pattern: /^[A-Z]{2}/,
        format: /^([A-Z]{2})(\d{0,3})([A-Z]{0,2})$/,
        description: 'XX-123-XX'
    },
    {
        pattern: /^\d{2}[A-Z]/,
        format: /^(\d{2})([A-Z]{0,3})(\d{0,1})$/,
        description: '99-XXX-9'
    },
    {
        pattern: /^\d{2}/,
        format: /^(\d{2})([A-Z]{0,2})(\d{0,2})$/,
        description: '99-XX-XX'
    },
    {
        pattern: /^\d{1}[A-Z]/,
        format: /^(\d{1})([A-Z]{0,3})(\d{0,2})$/,
        description: '9-XXX-99'
    },
    {
        pattern: /^[A-Z]\d/,
        format: /^([A-Z]{1})(\d{0,3})([A-Z]{0,2})$/,
        description: 'X-999-XX'
    },
    {
        pattern: /^[A-Z]{3}/,
        format: /^([A-Z]{3})(\d{0,2})([A-Z]{0,1})$/,
        description: 'XXX-99-X'
    },
    {
        pattern: /^[A-Z]\d{3}[A-Z]{2}$/,
        format: /^([A-Z]{1})(\d{0,3})([A-Z]{0,2})$/,
        description: 'X-999-XX'
    }
];
const sizeConfig = {
    small: { height: '40px', width: '100%', fontSize: 'sm' },
    medium: { height: '80px', width: '100%', fontSize: '34px' },
    large: { height: '80px', width: '100%', fontSize: '40px' },
};
const formatLicensePlate = (value) => {
    // Remove all non-alphanumeric characters and convert to uppercase
    const cleaned = value.replace(/[^A-Z0-9]/gi, '').toUpperCase();
    // Find matching format and apply it
    for (const { pattern, format } of PLATE_FORMATS) {
        if (pattern.test(cleaned)) {
            const parts = cleaned.match(format);
            if (parts) {
                return [parts[1], parts[2], parts[3]].filter(Boolean).join('-');
            }
        }
    }
    return cleaned;
};
export const LicensePlate = (_a) => {
    var { size = 'medium', text = '', isActive = false, isDisabled = false, isLoading = false, title, error = {
        message: 'Moet een geldig kenteken zijn.',
        color: 'red.500',
    }, onChange, onComplete, autoFocus = false } = _a, boxProps = __rest(_a, ["size", "text", "isActive", "isDisabled", "isLoading", "title", "error", "onChange", "onComplete", "autoFocus"]);
    const [inputValue, setInputValue] = useState(text);
    // Check if input is complete (has expected length)
    const cleanedLength = inputValue.replace(/-/g, '').length;
    const isComplete = cleanedLength >= 6 && cleanedLength <= 9;
    // Only validate format when input is complete
    const isValidFormat = PLATE_FORMATS.some(({ pattern, format }) => {
        const cleaned = inputValue.replace(/-/g, '');
        // First check if the pattern matches
        if (!pattern.test(cleaned))
            return false;
        // Then check if the format matches exactly
        const match = cleaned.match(format);
        if (!match)
            return false;
        // Check if all parts are present and have correct lengths
        const parts = match.slice(1).filter(Boolean);
        const totalLength = parts.join('').length;
        // Allow 6 to 9 characters total
        return totalLength >= 6 && totalLength <= 9;
    });
    const showValidation = isComplete;
    const isValid = isValidFormat;
    const currentSize = sizeConfig[size];
    const handleChange = (e) => {
        const value = e.target.value;
        const formattedValue = formatLicensePlate(value);
        setInputValue(formattedValue);
        if (formattedValue !== text) {
            onChange === null || onChange === void 0 ? void 0 : onChange(formattedValue);
        }
        // Check if the new value is complete and valid
        const newCleanedLength = formattedValue.replace(/-/g, '').length;
        const isNewValueComplete = newCleanedLength >= 6 && newCleanedLength <= 9;
        // Check if the format is valid
        const isValidNewFormat = PLATE_FORMATS.some(({ pattern, format }) => {
            const cleaned = formattedValue.replace(/-/g, '');
            if (!pattern.test(cleaned))
                return false;
            const match = cleaned.match(format);
            if (!match)
                return false;
            const parts = match.slice(1).filter(Boolean);
            const totalLength = parts.join('').length;
            return totalLength >= 6 && totalLength <= 9;
        });
        if (isNewValueComplete && isValidNewFormat) {
            onComplete === null || onComplete === void 0 ? void 0 : onComplete(formattedValue.toUpperCase());
        }
    };
    return (_jsxs(Box, Object.assign({ as: "div" }, boxProps, { children: [title && (_jsx(Heading, Object.assign({ size: "sm", mb: 2, as: "h3", sx: { marginBottom: '0.5rem' } }, { children: title }))), _jsxs(FormControl, Object.assign({ isDisabled: isDisabled || isLoading }, { children: [_jsx(FormLabel, Object.assign({ htmlFor: "license-plate-input", srOnly: true }, { children: "License Plate Number" })), _jsxs(Flex, Object.assign({ width: currentSize.width, height: currentSize.height, borderRadius: "md", overflow: "hidden", border: "1px solid", borderColor: showValidation
                            ? (isValid ? '#38A169' : error.color)
                            : (isActive ? '#345aa5' : '#13100D4D'), transition: "all 0.2s", opacity: isDisabled ? 0.6 : 1, "data-testid": "license-plate-container", animation: inputValue === '' ? 'shake 0.82s cubic-bezier(.36,.07,.19,.97) both' : undefined, sx: {
                            '@keyframes shake': {
                                '10%, 90%': {
                                    transform: 'translate3d(-1px, 0, 0)',
                                },
                                '20%, 80%': {
                                    transform: 'translate3d(2px, 0, 0)',
                                },
                                '30%, 50%, 70%': {
                                    transform: 'translate3d(-4px, 0, 0)',
                                },
                                '40%, 60%': {
                                    transform: 'translate3d(4px, 0, 0)',
                                },
                            }
                        } }, { children: [_jsx(Flex, Object.assign({ bg: "#345aa5", color: "white", px: 3, alignItems: "center", justifyContent: "center", fontSize: "16px", fontWeight: "bold" }, { children: "NL" })), _jsx(Input, { id: "license-plate-input", flex: 1, value: inputValue, onChange: handleChange, isDisabled: isDisabled || isLoading, placeholder: "XX-123-XX", bg: "#f7b518", border: "none", textAlign: "center", rounded: "none", height: "full", fontWeight: "bold", color: showValidation && !isValid ? 'error.color' : 'black', fontSize: currentSize.fontSize, maxLength: 9, _placeholder: { color: '#13100D4D' }, _disabled: { opacity: 0.7, cursor: 'not-allowed' }, _focus: {
                                    border: 'none',
                                    boxShadow: 'none',
                                    outline: 'none'
                                }, sx: {
                                    '&::-webkit-input-placeholder': { fontSize: currentSize.fontSize },
                                    '&::-moz-placeholder': { fontSize: currentSize.fontSize },
                                }, autoFocus: autoFocus }), isLoading && (_jsx(Flex, Object.assign({ px: 3, alignItems: "center", justifyContent: "center", bg: "#ffeb3b" }, { children: _jsx(Spinner, { size: size === 'small' ? 'sm' : 'md', color: "#003399", "data-testid": "license-plate-spinner" }) })))] })), showValidation && !isValid && (_jsx(Box, Object.assign({ color: error.color, fontSize: "sm", mt: 1 }, { children: error.message })))] }))] })));
};
//# sourceMappingURL=LicensePlate.js.map